import React, { useState } from 'react';
import { Wrench, Send, Loader2, X, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = window.__WR_API_BASE__;

const SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Minor', icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30', desc: 'Cosmetic or minor inconvenience' },
  { value: 'major', label: 'Major', icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30', desc: 'Feature broken but workaround exists' },
  { value: 'critical', label: 'Critical', icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', desc: 'Can\'t complete my work' },
];

const BugReportModal = ({ isOpen, onClose, guestId }) => {
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('minor');
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error('Please describe the issue');
      return;
    }
    setSending(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (guestId) headers['X-Guest-ID'] = guestId;

      const res = await fetch(`${API}/api/bug-reports`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          description: description.trim(),
          severity,
          page: window.location.pathname,
        }),
      });
      if (!res.ok) throw new Error('Failed to submit');
      toast.success("Thank you for reporting — we'll get that addressed.");
      setDescription('');
      setSeverity('minor');
      onClose();
    } catch (err) {
      toast.error('Failed to submit report. Try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-md bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="bug-report-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <Wrench className="w-4.5 h-4.5 text-amber-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Report an Issue</h3>
              <p className="text-[11px] text-muted-foreground">Help us fix problems faster</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary transition-colors" data-testid="bug-report-close">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Severity */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">How bad is it?</label>
            <div className="grid grid-cols-3 gap-2">
              {SEVERITY_OPTIONS.map(({ value, label, icon: Icon, color, bg, desc }) => (
                <button
                  key={value}
                  onClick={() => setSeverity(value)}
                  className={`flex flex-col items-center gap-1 p-2.5 rounded-lg border transition-all text-center ${
                    severity === value ? bg + ' ring-1 ring-current' : 'border-border bg-secondary/20 hover:bg-secondary/40'
                  }`}
                  data-testid={`severity-${value}`}
                >
                  <Icon className={`w-4 h-4 ${severity === value ? color : 'text-muted-foreground'}`} />
                  <span className={`text-xs font-semibold ${severity === value ? color : 'text-foreground'}`}>{label}</span>
                  <span className="text-[9px] text-muted-foreground leading-tight">{desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">What happened?</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what went wrong, what you expected, and what you were doing when it happened..."
              className="w-full h-28 px-3 py-2.5 text-sm bg-secondary/30 border border-border rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 text-foreground placeholder:text-muted-foreground/50"
              maxLength={1000}
              data-testid="bug-description-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1 text-right">{description.length}/1000</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border bg-secondary/10 flex items-center justify-between">
          <p className="text-[10px] text-muted-foreground">Reports go directly to the admin dashboard</p>
          <Button
            onClick={handleSubmit}
            disabled={sending || !description.trim()}
            className="gap-1.5"
            size="sm"
            data-testid="submit-bug-report"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Submit Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BugReportModal;
